import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

interface AdvancedAISettingsProps {
  isAIModalOpen: boolean;
  setIsAIModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  temperature: number;
  setTemperature: React.Dispatch<React.SetStateAction<number>>;
  maximumLength: number;
  setMaximumLength: React.Dispatch<React.SetStateAction<number>>;
  stopSequences: string;
  setStopSequences: React.Dispatch<React.SetStateAction<string>>;
  topP: number;
  setTopP: React.Dispatch<React.SetStateAction<number>>;
  fPenalty: number;
  setFPenalty: React.Dispatch<React.SetStateAction<number>>;
  pPenalty: number;
  setPPenalty: React.Dispatch<React.SetStateAction<number>>;
  temperatureConfig: { min: string; max: string; steps: string };
  maximumLengthConfig: { min: number; max: number; steps: number };
  topPConfig: { min: number; max: number; steps: number };
  fPenaltyConfig: { min: number; max: number; steps: number };
  pPenaltyConfig: { min: number; max: number; steps: number };
  modal: string;
  setModal: React.Dispatch<React.SetStateAction<string>>; 
  aiModels: any;
}

interface Model {
  value: string;
  name: string;
  provider: string;
  max_length?: number;
}

const AdvancedAISettings: React.FC<AdvancedAISettingsProps> = ({
  isAIModalOpen,
  setIsAIModalOpen,
  temperature,
  setTemperature,
  maximumLength,
  setMaximumLength,
  stopSequences,
  setStopSequences,
  topP,
  setTopP,
  fPenalty,
  setFPenalty,
  pPenalty,
  setPPenalty,
  temperatureConfig,
  maximumLengthConfig,
  topPConfig,
  fPenaltyConfig,
  pPenaltyConfig,
  modal,
  setModal,
  aiModels
}) => {
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);

  useEffect(() => {
    if (aiModels && aiModels.length > 0 && modal) {
      const currentModel = aiModels.find((model: Model) => model.name === modal);
      if (currentModel) {
        setSelectedModel(currentModel);
        // Update maximum length based on the selected model if it has a max_length property
        if (currentModel.max_length !== undefined) {
          setMaximumLength(currentModel.max_length);
        }
      }
    }
  }, [modal, aiModels]);

  const handleOverlayClick = () => {
    setIsAIModalOpen(false);
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
  };

  const handleTemperatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTemperature(parseFloat(event.target.value));
  };

  const handleStopSequences = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStopSequences(event.target.value);
  };

  const handleTopPChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTopP(parseFloat(event.target.value));
  };

  const handleFPenaltyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFPenalty(parseFloat(event.target.value));
  };

  const handlePPenaltyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPPenalty(parseFloat(event.target.value));
  };

  const handleModelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedModelName = event.target.value;
    setModal(selectedModelName);
    
    // Find the selected model and update maximum length
    const model = aiModels.find((m: Model) => m.value === selectedModelName);
    if (model && model.max_length !== undefined) {
      setMaximumLength(model.max_length);
    }
  };
  
  React.useEffect(() => {
    if (!modal && aiModels.length > 0) {
      const defaultModel = aiModels.find((model: { is_default: any; }) => model.is_default);
      if (defaultModel) {
        setModal(defaultModel.name);
        if (defaultModel.max_length !== undefined) {
          setMaximumLength(defaultModel.max_length);
        }
      }
    }
  }, [modal, aiModels]);

  return (
    <div
  className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-50 ${isAIModalOpen ? 'block' : 'hidden'}`}
  onClick={handleOverlayClick}
>
  <div
    className="bg-white w-[25%] h-[95%] shadow-lg transform transition-transform duration-300 ease-in-out"
    onClick={handleModalClick}
  >
    <div className="h-full flex flex-col">
      {/* Sticky Header */}
      <div className="p-6 border-b border-gray-200 bg-white z-10">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Advanced AI Settings</h2>
          <button onClick={() => setIsAIModalOpen(false)} className="text-black-500 hover:text-red-700">
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>
        <p className="sub-heading mt-2">Adjust hyper-parameters to further tune your chatbot.</p>
      </div>

      {/* Scrollable Body - all your settings should be inside this */}
      <div className="overflow-y-auto flex-grow px-6 pb-6 pt-4 space-y-6  mr-4  ">
      {/* Keep all your existing sliders/fields/components inside here */}
      {/* For example: */}
      {/* <hr className="pb-4" /> */}
            <label className="pt-5 pl-2 model-label">Model</label>
            <div className="flex items-center">
              <select 
                className="border border-gray-400 mx-1.5 pl-2 text-left w-[100%] h-[35px]"  
                onChange={handleModelChange}
                value={modal}
              >
                {aiModels.map((model: Model) => (
                  <option 
                    key={model.value} 
                    value={model.value} 
                    className="bg-white hover:bg-black hover:text-white"
                  >
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
          

          <div className="pt-3">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">Temperature</label>
              <span className="text-sm text-gray-700 border border-gray-300 rounded-lg p-2 pl-9 w-[20%] text-right">
                {temperature.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center mt-2">
              <input
                id="temperature-slider"
                type="range"
                min={temperatureConfig.min}
                max={temperatureConfig.max}
                step={temperatureConfig.steps}
                value={temperature}
                onChange={handleTemperatureChange}
                className="w-full temperature-slider"
              />
            </div>
          </div>

          <div className="pt-2">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">Maximum Length</label>
              <input
                type="text"
                value={maximumLength}
                disabled
                className="text-sm text-gray-700 border border-gray-300 rounded-lg p-2 w-[25%] text-right bg-gray-100"
              />
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500 italic pl-2">
                Maximum length is determined by the selected model and cannot be modified.
              </p>
            </div>
          </div>

          <div className="pt-2">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">Stop Sequences</label>
            </div>
            <div className="flex items-center mt-2">
              <input
                type="text"
                id="stopSequences"
                name="stopSequences"
                className="newassistant-input"
                value={stopSequences}
                onChange={handleStopSequences}
              />
            </div>
          </div>

          <div className="pt-1">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">TopP</label>
              <span className="text-sm text-gray-700 border border-gray-300 rounded-lg p-2 w-[20%] text-right">
                {topP.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center mt-2">
              <input
                id="topP-slider"
                type="range"
                min={topPConfig.min}
                max={topPConfig.max}
                step={topPConfig.steps}
                value={topP}
                onChange={handleTopPChange}
                className="w-full temperature-slider"
              />
            </div>
          </div>

          <div className="pt-1">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">Frequency Penalty</label>
              <span className="text-sm text-gray-700 border border-gray-300 rounded-lg p-2 w-[20%] text-right">
                {fPenalty.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center mt-2">
              <input
                id="fPenalty-slider"
                type="range"
                min={fPenaltyConfig.min}
                max={fPenaltyConfig.max}
                step={fPenaltyConfig.steps}
                value={fPenalty}
                onChange={handleFPenaltyChange}
                className="w-full temperature-slider"
              />
            </div>
          </div>

          <div className="pt-1">
            <div className="flex justify-between items-center pt-2 pl-2">
              <label className="model-label">Presence Penalty</label>
              <span className="text-sm text-gray-700 border border-gray-300 rounded-lg p-2 w-[20%] text-right">
                {pPenalty.toFixed(1)}
              </span>
            </div>
            <div className="flex items-center mt-2">
              <input
                id="pPenalty-slider"
                type="range"
                min={pPenaltyConfig.min}
                max={pPenaltyConfig.max}
                step={pPenaltyConfig.steps}
                value={pPenalty}
                onChange={handlePPenaltyChange}
                className="w-full temperature-slider"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AdvancedAISettings;